import { useMemo, useState } from "react";
import { readChangeSetHead } from "../../../../api/content/changeSetsApi";
import { answerHumanStep, newClientKey } from "../../../../api/threads/threadsApi";
import { answeringStep, type ChangeSetStep } from "../../../processAuthoring/draft/changeSetStep";
import { readShippedWorkflow, type ShippedWorkflow } from "../shippedWorkflow";
import { startEdit } from "../startCommands";
import { formatEditedItem, type EditedItem } from "./editedItem";

/** Answer the edit step requested in `entry` with the change set at its current head, as the step asks (P3's `propose_change`). */
export async function answerWithChangeSet(threadId: string, entry: number, changeSetId: string, clientRequestId: string): Promise<void> {
  const head = await readChangeSetHead(changeSetId);
  await answerHumanStep(threadId, {
    answersEntry: entry,
    comment: null,
    choice: null,
    result: { output_kind: "change_set", change_set_id: changeSetId, head, collection: "project_content" },
    clientRequestId,
  });
}

export interface EditBeforeThread {
  readonly projectId: string;
  /** The Thread's title, as the preparation generates it (F31). */
  readonly title: string;
  /** `null` while there is nothing the person may edit; a save then fails. */
  readonly edited: EditedItem | null;
  /** The preparation's key; without one, each saved edit gets a key, and so a Thread, of its own. */
  readonly requestKey?: string;
}

/**
 * The "Edit an item" step before its Thread exists (ADR-0031 D2, WA-11): an editor shown in
 * place or in the preparation saves through it. Opening the editor creates nothing; its save
 * starts the edit's Thread, asks the edit step and answers it with the change set, and the
 * Thread's review tail decides it. A save tried again finds the same Thread by the edit's key,
 * so it starts with what the first try read: the same Workflow and model version.
 */
export function useStepBeforeThread({ projectId, title, edited, requestKey }: EditBeforeThread): ChangeSetStep {
  const [ownKey, setOwnKey] = useState(newClientKey);
  const key = requestKey ?? ownKey;
  const item = edited === null ? null : formatEditedItem(edited);
  return useMemo(() => {
    // Read once for this edit's key: the memo is made again for a new key.
    const shipped: { read: Promise<ShippedWorkflow> | null } = { read: null };
    return answeringStep(async (changeSetId) => {
      if (item === null) throw new Error("There is nothing here this project can edit.");
      shipped.read ??= readShippedWorkflow(projectId, "edit_item");
      const { workflow, contentVersion } = await shipped.read.catch((caught: unknown) => {
        shipped.read = null;
        throw caught;
      });
      if (workflow === null) throw new Error("This project runs no \"Edit an item\" Workflow; it pins no DMW library.");
      const started = await startEdit({ projectId, title, workflow: workflow.reference, contentVersion, item, requestKey: key });
      await answerWithChangeSet(started.threadId, started.entry, changeSetId, `${key}-answer`);
      if (requestKey === undefined) setOwnKey(newClientKey());
      return started.threadId;
    }, null);
  }, [projectId, title, item, key, requestKey]);
}
